import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  PartyPopper,
  CheckCircle2,
  XCircle,
  Flame,
  Loader2,
  ClipboardList,
  RotateCcw,
} from "lucide-react";
import PageBlobs from "@/components/PageBlobs";
import ReviewPrompt from "@/components/ReviewPrompt";
import SessionDownloadButton, { hasPremiumAnalytics } from "@/components/SessionDownload";
import { apiFetch, getToken } from "@/api";

const VERDICT_LABEL = {
  correct: "Correct",
  partially_correct: "Partially Correct",
  incorrect: "Incorrect",
};

const fmt = (v) =>
  typeof v === "number" ? (Number.isInteger(v) ? String(v) : v.toFixed(1)) : "—";

/* Score: objective correct = 1; theory = AI marks (1 / 0.5 / 0; unevaluated = 0).
   Exam papers: objective correct = question marks; theory = AI fraction × marks. */
const computeScore = (qs, fallback, isExam = false) => {
  if (!Array.isArray(qs) || !qs.length) return fallback;
  const s = qs.reduce((sum, q) => {
    if (q.question_type === "STATEMENT_THEORY") {
      if (isExam) return sum + (typeof q.earned === "number" ? q.earned : 0);
      return sum + (typeof q.marks === "number" ? q.marks : 0);
    }
    if (isExam) return sum + (q.is_correct === true ? q.marks || 1 : 0);
    return sum + (q.is_correct === true ? 1 : 0);
  }, 0);
  return Math.round(s * 2) / 2;
};

const VerdictChip = ({ q, i }) => {
  if (q.question_type === "STATEMENT_THEORY") {
    if (!q.evaluation)
      return (
        <span
          data-testid={`eval-verdict-${i}`}
          className="shrink-0 rounded-full bg-[#17201A]/8 px-3 py-1 text-[10px] font-bold text-[#647067]"
        >
          Not evaluated
        </span>
      );
    const styles = {
      correct: "bg-[#16A34A]/10 text-[#16A34A]",
      partially_correct: "bg-[#FACC15]/25 text-[#92600A]",
      incorrect: "bg-red-50 text-red-500",
    };
    return (
      <span
        data-testid={`eval-verdict-${i}`}
        className={`shrink-0 rounded-full px-3 py-1 text-[10px] font-bold ${styles[q.evaluation]}`}
      >
        {VERDICT_LABEL[q.evaluation]} · {fmt(q.marks)}/1
      </span>
    );
  }
  return q.is_correct === true ? (
    <span
      data-testid={`eval-verdict-${i}`}
      className="shrink-0 rounded-full bg-[#16A34A]/10 px-3 py-1 text-[10px] font-bold text-[#16A34A]"
    >
      Correct · 1/1
    </span>
  ) : (
    <span
      data-testid={`eval-verdict-${i}`}
      className="shrink-0 rounded-full bg-red-50 px-3 py-1 text-[10px] font-bold text-red-500"
    >
      Wrong · 0/1
    </span>
  );
};

const PracticeComplete = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { subject, chapter, board, klass, difficulty, correct, wrong, total, review, exam } =
    location.state || {};
  const posted = useRef(false);
  const [streak, setStreak] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [user, setUser] = useState(null);
  const [evaluated, setEvaluated] = useState(Array.isArray(review) ? review : null);
  const [evaluating, setEvaluating] = useState(false);
  const [showEval, setShowEval] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [reattempting, setReattempting] = useState(false);

  const startReattempt = async () => {
    setReattempting(true);
    try {
      const data = await apiFetch("/reattempt", { method: "POST" });
      const r = data.rationale || {};
      navigate("/practice", {
        state: {
          questions: data.questions,
          subject: r.subject,
          chapter: r.chapter,
          board,
          klass,
          mode: "reattempt",
          reason: r.reason,
        },
      });
    } catch {
      setReattempting(false);
    }
  };

  useEffect(() => {
    if (!getToken()) return;
    apiFetch("/me").then(setUser).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (posted.current || !subject) return;
    posted.current = true;
    const qs = Array.isArray(review) ? review.map((q) => ({ ...q })) : null;

    const save = () => {
      if (!getToken()) return;
      apiFetch("/practice-sessions", {
        method: "POST",
        body: {
          board: board || "CBSE",
          klass: String(klass || "10"),
          subject,
          chapter,
          difficulty: difficulty || "Medium",
          questionsCompleted: total || (qs ? qs.length : 5),
          questionsCorrect: computeScore(
            qs,
            typeof correct === "number" ? correct : null,
            Boolean(exam)
          ),
          questions: qs || undefined,
        },
      })
        .then((d) => {
          setStreak(d.currentStreak);
          if (d.id) setSessionId(d.id);
          apiFetch("/reviews/should-prompt")
            .then((r) => {
              if (r.show) setShowRating(true);
            })
            .catch(() => {});
        })
        .catch(() => {});
    };

    const theoryIdx =
      qs && getToken()
        ? qs
            .map((q, i) =>
              q.question_type === "STATEMENT_THEORY" &&
              q.student_answer &&
              String(q.student_answer).trim()
                ? i
                : -1
            )
            .filter((i) => i >= 0)
        : [];

    if (!theoryIdx.length) {
      setEvaluated(qs);
      save();
      return;
    }

    // Batch-evaluate all theory answers first, THEN save the session with verdicts
    setEvaluating(true);
    apiFetch("/evaluate-theory", {
      method: "POST",
      body: {
        board: board || "CBSE",
        klass: String(klass || "10"),
        subject,
        chapter,
        difficulty: difficulty || "Medium",
        items: theoryIdx.map((i) => ({
          question: qs[i].question,
          student_answer: qs[i].student_answer,
          model_answer: qs[i].answer,
        })),
      },
    })
      .then((d) => {
        (d.evaluations || []).forEach((ev) => {
          const q = qs[theoryIdx[ev.index]];
          if (q) {
            q.evaluation = ev.verdict;
            if (exam) q.earned = Math.round(ev.marks * (q.marks || 1) * 2) / 2;
            else q.marks = ev.marks;
            q.feedback = ev.feedback;
          }
        });
      })
      .catch(() => {})
      .finally(() => {
        setEvaluating(false);
        setEvaluated([...qs]);
        save();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!subject || !chapter) return <Navigate to="/practice-setup" replace />;

  const hasReview = Array.isArray(evaluated) && evaluated.length > 0;
  const hasScores = typeof correct === "number" && typeof wrong === "number";
  const pdfOk = hasPremiumAnalytics(user);
  const score = computeScore(evaluated, typeof correct === "number" ? correct : null, Boolean(exam));
  const totalQ = total || (hasReview ? evaluated.length : 5);

  const counts = (evaluated || []).reduce(
    (acc, q) => {
      if (q.question_type === "STATEMENT_THEORY") {
        if (q.evaluation === "correct") acc.correct += 1;
        else if (q.evaluation === "partially_correct") acc.partial += 1;
        else if (q.evaluation === "incorrect") acc.wrong += 1;
      } else if (q.is_correct === true) acc.correct += 1;
      else acc.wrong += 1;
      return acc;
    },
    { correct: 0, partial: 0, wrong: 0 }
  );

  return (
    <div
      data-testid="practice-complete-page"
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#F8FAF7] px-6 py-10 font-body text-[#17201A]"
    >
      <PageBlobs />
      <div className="relative flex w-full max-w-md flex-col gap-5">
      <div className="rounded-[2rem] border border-[#E3ECE6] bg-white p-10 text-center shadow-sm">
        <div className="mx-auto mb-6 inline-flex rounded-full bg-[#FACC15]/25 p-5">
          <PartyPopper className="h-8 w-8 text-[#16A34A]" />
        </div>
        <h1
          data-testid="complete-title"
          className="font-display text-3xl font-extrabold sm:text-4xl"
        >
          {exam ? "Exam Complete!" : "Practice Complete! 🎉"}
        </h1>

        <div
          data-testid="complete-summary"
          className="mt-6 rounded-2xl bg-[#F8FAF7] p-5 text-left"
        >
          <div className="flex items-center gap-2 text-sm font-bold">
            <CheckCircle2 className="h-4 w-4 text-[#16A34A]" />
            {exam ? `${exam.label} · ${totalQ} questions` : `${totalQ} questions completed`}
          </div>
          {hasReview ? (
            <div data-testid="complete-scores" className="mt-3 flex flex-wrap gap-2.5">
              <span
                data-testid="complete-correct"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#16A34A]/10 px-3.5 py-1.5 text-xs font-bold text-[#16A34A]"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                {counts.correct} Correct
              </span>
              {counts.partial > 0 && (
                <span
                  data-testid="complete-partial"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#FACC15]/25 px-3.5 py-1.5 text-xs font-bold text-[#92600A]"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {counts.partial} Partial
                </span>
              )}
              <span
                data-testid="complete-wrong"
                className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3.5 py-1.5 text-xs font-bold text-red-500"
              >
                <XCircle className="h-3.5 w-3.5" />
                {counts.wrong} Wrong
              </span>
            </div>
          ) : (
            hasScores && (
              <div data-testid="complete-scores" className="mt-3 flex gap-2.5">
                <span
                  data-testid="complete-correct"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#16A34A]/10 px-3.5 py-1.5 text-xs font-bold text-[#16A34A]"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {correct} Correct
                </span>
                <span
                  data-testid="complete-wrong"
                  className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3.5 py-1.5 text-xs font-bold text-red-500"
                >
                  <XCircle className="h-3.5 w-3.5" />
                  {wrong} Wrong
                </span>
              </div>
            )
          )}
          {evaluating ? (
            <p
              data-testid="evaluating-indicator"
              className="mt-3 inline-flex items-center gap-2 text-xs font-bold text-[#647067]"
            >
              <Loader2 className="h-3.5 w-3.5 animate-spin text-[#16A34A]" />
              AI is evaluating your theory answers…
            </p>
          ) : (
            score !== null && (
              <p
                data-testid="complete-score"
                className="mt-3 font-display text-lg font-extrabold text-[#17201A]"
              >
                Score: {fmt(score)}/{exam ? exam.totalMarks : totalQ}
              </p>
            )
          )}
          {exam && (
            <p data-testid="exam-meta" className="mt-2 text-xs font-bold text-[#647067]">
              Accuracy: {totalQ ? Math.round((counts.correct / totalQ) * 100) : 0}% · Time
              taken: {Math.floor((exam.timeTakenSec || 0) / 60)}m {(exam.timeTakenSec || 0) % 60}s
              {typeof exam.timeLimitSec === "number" && (
                <> of {Math.floor(exam.timeLimitSec / 60)}m allowed</>
              )}
            </p>
          )}
          <p className="mt-2 text-sm text-[#17201A]/70">
            {subject} · {chapter}
          </p>
          {/* Recommended next action — one clear step, not analytics */}
          {!evaluating && (
            <p data-testid="recommended-next-action" className="mt-3 rounded-xl bg-[#F8FAF7] px-4 py-2.5 text-xs font-semibold leading-relaxed text-[#17201A]/80">
              {counts.wrong > 0
                ? `Next: re-fix your ${counts.wrong} missed question${counts.wrong === 1 ? "" : "s"} — ${chapter} needs one more pass.`
                : `Next: raise the difficulty or start a new ${subject} chapter — you're ready.`}
            </p>
          )}
          {streak !== null && (
            <p
              data-testid="complete-streak"
              className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#FACC15]/25 px-3.5 py-1.5 text-xs font-bold text-[#17201A]"
            >
              <Flame className="h-3.5 w-3.5 text-[#FACC15]" />
              {streak}-day streak
            </p>
          )}
        </div>

        <div className="mt-8 grid gap-3">
          {counts.wrong > 0 && (
            <button
              data-testid="practice-mistakes-btn"
              onClick={startReattempt}
              disabled={reattempting}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#14532D] py-4 text-sm font-bold text-white shadow-sm transition-[background-color,transform,opacity] duration-200 hover:bg-[#16A34A] disabled:opacity-50"
            >
              {reattempting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4" />
              )}
              Practice your mistakes →
            </button>
          )}
          {hasReview && (
            <button
              data-testid="view-evaluation-btn"
              onClick={() => setShowEval(true)}
              disabled={evaluating}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-[#16A34A] bg-white py-4 text-sm font-bold text-[#16A34A] transition-[background-color,transform] duration-200 hover:bg-[#16A34A]/5 disabled:opacity-50"
            >
              <ClipboardList className="h-4 w-4" />
              View Detailed Evaluation
            </button>
          )}
          <SessionDownloadButton
            sessionId={sessionId}
            paid={pdfOk}
            onLockedClick={() => navigate("/pricing")}
          />
          <button
            data-testid="practice-another-btn"
            onClick={() => navigate("/practice-setup", { state: { subject } })}
            className="w-full rounded-full bg-[#16A34A] py-4 text-sm font-bold text-white shadow-sm transition-[background-color,transform] duration-200 hover:bg-[#15803D]"
          >
            Practice Another Topic
          </button>
          <button
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate("/dashboard")}
            className="w-full rounded-full border border-[#E3ECE6] py-4 text-sm font-bold text-[#17201A] transition-[border-color,transform] duration-200 hover:border-[#FACC15]"
          >
            Back to Dashboard
          </button>
        </div>
      </div>

      {showRating && (
        <ReviewPrompt onClose={() => setShowRating(false)} />
      )}
      </div>

      {showEval && hasReview && (
        <div
          data-testid="evaluation-modal"
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-6"
          onClick={() => setShowEval(false)}
        >
          <div
            className="animate-fade-up flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-t-[2rem] bg-white shadow-xl sm:rounded-[2rem]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-[#E3ECE6] p-6 sm:p-7">
              <h2 className="font-display text-xl font-extrabold">
                Detailed Evaluation
              </h2>
              <p
                data-testid="evaluation-modal-score"
                className="mt-1 text-xs font-semibold text-[#647067]"
              >
                {subject} · {chapter} · Score {fmt(score)}/{totalQ}
              </p>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto p-6 sm:p-7">
              {evaluated.map((q, i) => (
                <div
                  key={i}
                  data-testid={`eval-question-${i}`}
                  className="rounded-2xl border border-[#E3ECE6] p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm font-bold leading-snug text-[#17201A]">
                      Q{i + 1}. {q.question}
                    </p>
                    <VerdictChip q={q} i={i} />
                  </div>
                  {q.student_answer != null && (
                    <p className="mt-2.5 text-xs font-semibold text-[#647067]">
                      Your answer:{" "}
                      <span className="text-[#17201A]">{q.student_answer}</span>
                    </p>
                  )}
                  <p className="mt-1 text-xs font-semibold text-[#647067]">
                    {q.question_type === "STATEMENT_THEORY"
                      ? "Model answer"
                      : "Correct answer"}
                    : <span className="text-[#17201A]">{q.answer}</span>
                  </p>
                  {q.feedback && (
                    <p
                      data-testid={`eval-feedback-${i}`}
                      className="mt-2 rounded-xl bg-[#F8FAF7] px-3 py-2 text-xs font-semibold leading-relaxed text-[#17201A]/80"
                    >
                      AI feedback: {q.feedback}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <div className="border-t border-[#E3ECE6] p-5 sm:px-7">
              <button
                data-testid="evaluation-close-btn"
                onClick={() => setShowEval(false)}
                className="w-full rounded-full bg-[#16A34A] py-3 text-sm font-bold text-white transition-[background-color,transform] duration-200 hover:bg-[#15803D]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PracticeComplete;
